/**
 * Global language change cache invalidator for RTK Query
 * Listens for language change events and invalidates all metadata caches
 */
import { baseApi } from './RTK/Api';

const LANGUAGE_CHANGE_EVENT = 'app-language-changed';

// All metadata tag types that should be invalidated on language change
const METADATA_TAG_TYPES = [
  { type: 'Lookup', id: 'META' },
  { type: 'SettingsMeta' },
  { type: 'EmployeeDelegationMeta' },
  { type: 'EmployeeExceptionMeta' },
  { type: 'TimePeriod' },
  { type: '$RecalculateAttendance' },
  { type: 'CompanyMeta' },
];

// Common table codes used across the application
const LOOKUP_TABLE_CODES = [
  'Employee', 'Company', 'EmployeeException', 'EmployeeDelegation',
  'Nationality', 'Position', 'Department', 'Section', 'Unit',
  'Religion', 'ContractType', 'EmployeeCategory', 'Gender',
  'JobLevel', 'Project', 'CostCenter', 'Location', 'Shift',
  'ExceptionType', 'DelegationType', 'RequestType', 'PermissionType',
  'VacationType',  'TicketType', 'ResignationType',
  'ALL'
];

const SETTINGS_TABLE_CODES = [
  'RequestType', 'PermissionType', 'VacationType', 
  , 'ResignationType', 'ExceptionType',
  'ALL'
];

/**
 * Invalidate all metadata caches
 * This forces RTK Query to refetch metadata when language changes
 */
const invalidateMetadataCaches = (dispatch) => {
  // Collect all tags to invalidate in a single batch
  const tagsToInvalidate = [];

  // Add all metadata tag types with their specific IDs
  METADATA_TAG_TYPES.forEach((tag) => {
    if (tag.id) {
      tagsToInvalidate.push({ type: tag.type, id: tag.id });
    } else {
      tagsToInvalidate.push(tag.type);
    }
  });

  // Lookup metadata variations (covers getLookupMetadata with different tableCodes)
  LOOKUP_TABLE_CODES.forEach((code) => {
    tagsToInvalidate.push({ type: 'Lookup', id: `META-${code}` });
  });

  // Settings metadata variations (covers getSettingsMetaData with different tableCodes)
  SETTINGS_TABLE_CODES.forEach((code) => {
    tagsToInvalidate.push({ type: 'SettingsMeta', id: code });
  });

  // TimePeriod metadata variations
  LOOKUP_TABLE_CODES.forEach((code) => {
    tagsToInvalidate.push({ type: 'TimePeriod', id: `META-${code}` });
  });

  // RecalculateAttendance metadata variations
  LOOKUP_TABLE_CODES.forEach((code) => {
    tagsToInvalidate.push({ type: '$RecalculateAttendance', id: `META-${code}` });
  });

  // Company metadata variations
  LOOKUP_TABLE_CODES.forEach((code) => {
    tagsToInvalidate.push({ type: 'CompanyMeta', id: `META-${code}` });
  });

  // Single batch invalidation for all tags
  // console.log('[Language Cache Invalidator] Dispatching invalidateTags for:', tagsToInvalidate);
  const result = dispatch(baseApi.util.invalidateTags(tagsToInvalidate));
 
};

// Event for pages to listen to and manually refetch if needed
export const METADATA_REFETCH_EVENT = 'metadata-refetch-required';

/**
 * Broadcast a metadata refetch event for pages to handle manually
 */
const broadcastMetadataRefetchEvent = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(METADATA_REFETCH_EVENT, {
      detail: {
        timestamp: Date.now(),
        reason: 'language-change',
      },
    })
  );
  // console.log('[Language Cache Invalidator] Broadcasted metadata-refetch-required event');
};

/**
 * Initialize the language change listener
 * Call this once when the app starts (e.g., in store.js or app entry point)
 */
export const initLanguageCacheInvalidator = (store) => {
  if (typeof window === 'undefined') return;

  const handleLanguageChange = (event) => {
    const { locale, dir, timestamp } = event?.detail || {};
    // console.log(`[Language Cache Invalidator] Language changed to: ${locale} (${dir}) at ${timestamp}`);

    // Small delay to ensure all components have re-subscribed to their queries
    // after the language change loading state completes
    setTimeout(() => {
      // console.log('[Language Cache Invalidator] Executing cache invalidation...');
      // Invalidate all metadata caches to force refetch with new language
      invalidateMetadataCaches(store.dispatch);

      // Also broadcast an event for pages to manually refetch if tag invalidation doesn't work
      broadcastMetadataRefetchEvent();
    }, 100);
  };

  // Listen for language change events
  window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);

  // console.log('[Language Cache Invalidator] Initialized');

  // Return cleanup function
  return () => {
    window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
  };
};

export default initLanguageCacheInvalidator;
